import { Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { IExtendedLayout, ILayout, MimeTypeEnum } from 'src/app/models/defines';
import { HttpErrorResponse } from '@angular/common/http';
import { IDesignGroup, IExtendedDesignGroup } from '../models/design.model';
import { v4 as uuidv4 } from 'uuid';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import cloneDeep from 'lodash/cloneDeep';

@Injectable({
    providedIn: 'root',
})
export class FunctionsHelperService {
    constructor(private sanitizer: DomSanitizer) {}

    public blobToArrayBufferAsync(blob: Blob): Promise<ArrayBuffer> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = () => {
                if (reader.result instanceof ArrayBuffer) {
                    resolve(reader.result);
                } else {
                    reject(new Error('Failed to convert Blob to ArrayBuffer'));
                }
            };

            reader.onerror = () => {
                reject(reader.error);
            };

            reader.readAsArrayBuffer(blob);
        });
    }

    public transformLocalUrlToSafe(url: string): string {
        return this.sanitizer.sanitize(
            SecurityContext.RESOURCE_URL,
            this.sanitizer.bypassSecurityTrustResourceUrl(url)
        );
    }

    public deepClone<T>(source: T): T {
        return JSON.parse(JSON.stringify(source));
        // return cloneDeep(source);
    }
    // public deepClone<T>(source: T): T {
    //     const visited = new Map();
    //
    //     function _clone<T>(obj: T): T {
    //         if (obj === null || typeof obj !== 'object') {
    //             return obj;
    //         }
    //
    //         if (visited.has(obj)) {
    //             return visited.get(obj);
    //         }
    //
    //         let clone: any;
    //         if (Array.isArray(obj)) {
    //             clone = [];
    //         } else if (obj instanceof Date) {
    //             return new Date(obj.getTime()) as any;
    //         } else {
    //             clone = {};
    //         }
    //
    //         visited.set(obj, clone);
    //
    //         for (const key in obj) {
    //             if (Object.prototype.hasOwnProperty.call(obj, key)) {
    //                 clone[key] = _clone((obj as any)[key]);
    //             }
    //         }
    //
    //         return clone;
    //     }
    //
    //     return _clone(source);
    // }

    // Assuming 'arrayBuffer' is your ArrayBuffer
    public arrayBufferToBlob(arrayBuffer: ArrayBuffer, type: 'video/mp4' | 'audio/mp3'): Blob {
        return new Blob([arrayBuffer], { type: type });
    }

    public getMimeTypeFromExtension(url: string): MimeTypeEnum {
        const extension = url.split('.').pop() || '';
        switch (extension.toLowerCase()) {
            case 'webm':
                return MimeTypeEnum.VideoWebm;
            case 'mp4':
                return MimeTypeEnum.VideoMp4;
            // Add more cases for other video formats if needed
            default:
                return MimeTypeEnum.OCTECT_STREAM; // Default MIME type
        }
    }

    isHLSSupported() {
        const video = document.createElement('video');

        // Check for native HLS support
        if (
            video.canPlayType('application/vnd.apple.mpegURL') !== '' ||
            video.canPlayType('application/x-mpegURL') !== ''
        ) {
            return true;
        }

        // Check for MSE support
        if (window.MediaSource) {
            return true;
        }

        return false;
    }

    moveToBeginning<T>(array: T[], criteria: (item: T) => boolean): T[] {
        if (!array || array.length === 0) {
            return array;
        }
        // Separate items matching the criteria and those that don't
        const matchingItems = array.filter(criteria);
        const nonMatchingItems = array.filter((item) => !criteria(item));

        // Concatenate the matching items at the beginning
        return [...matchingItems, ...nonMatchingItems];
    }

    getDataAsync<T>(subject$: BehaviorSubject<T | null>): Promise<T> {
        const currentValue = subject$.value;
        if (currentValue !== null && currentValue !== undefined) {
            return Promise.resolve(currentValue); // Return cached value if available, including `false`
        }

        return new Promise<T>((resolve, reject) => {
            const subscription = subject$.subscribe({
                next: (value) => {
                    if (value !== null && value !== undefined) {
                        resolve(value); // Resolve when a valid value (including `false`) is emitted
                        subscription.unsubscribe(); // Avoid memory leaks
                    }
                },
                error: (error) => {
                    reject(error); // Reject on error
                    subscription.unsubscribe(); // Cleanup
                },
            });
        });
    }
}

export function catchErrorTypedAsync<T, E extends new (message?: string) => Error | HttpErrorResponse>(
    promise: Promise<T>,
    errorsToCatch: E[] = []
): Promise<{ data: T | null; error: InstanceType<E> | null }> {
    return promise
        .then((data) => ({
            data,
            error: null,
        }))
        .catch((error) => {
            /// We return any kind of error and not throwing
            if (errorsToCatch.length === 0) {
                return {
                    data: null,
                    error: error,
                };
            }
            const matchedErrorType = errorsToCatch.find((errorType) => error instanceof errorType);
            if (matchedErrorType) {
                return {
                    data: null,
                    error: new matchedErrorType(error.message) as InstanceType<E>,
                };
            }
            /// It means that we don't want to catch the error that was thrown to here (maybe syntax error etc)
            throw error;
        });
}
export function createIconLayer(
    suggestedFormat: IDesignGroup,
    whereTo: 'design-list' | 'video-idea' | 'library' | 'project-template'
) {
    const id = uuidv4();
    const iconLayout: IExtendedLayout = {
        _id: id,
        lottiePath: suggestedFormat.chosenDesign?.icon ?? '',
        relativeLottiePath: '',
        baseDesignPath: suggestedFormat.basePath,
    };
    switch (whereTo) {
        case 'design-list':
            iconLayout.lottiePath = suggestedFormat.videoIcon;
            break;
        case 'library':
        case 'project-template':
            iconLayout.lottiePath = suggestedFormat.minimalIcon;
            break;
        case 'video-idea':
            iconLayout.lottiePath = suggestedFormat.ideaIcon;
            break;
        default:
            iconLayout.lottiePath = suggestedFormat.minimalIcon;
            break;
    }
    iconLayout.relativeLottiePath = suggestedFormat.baseThumbnailsPath + iconLayout.lottiePath;
    return iconLayout;
}
export function convertHexToRgba(hex, alpha = 1) {
    // Remove hash if present
    hex = hex.replace(/^#/, '');

    // Parse r, g, b values
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
export function timeAgo(date: number): string {
    const now = new Date();
    const past = new Date(date);
    const millisecondsPerDay = 24 * 60 * 60 * 1000; // Number of milliseconds in a day
    const daysAgo = Math.floor((now.getTime() - past.getTime()) / millisecondsPerDay);
    const weeksAgo = Math.floor(daysAgo / 7);
    const monthsAgo = Math.floor(daysAgo / 30); // Approximate number of days in a month
    const yearsAgo = Math.floor(daysAgo / 365); // Approximate number of days in a year

    // Handle different time spans
    if (daysAgo === 0) {
        return 'Today';
    } else if (daysAgo === 1) {
        return 'Yesterday';
    } else if (daysAgo < 7) {
        return `${daysAgo} days ago`;
    } else if (weeksAgo < 4) {
        return `${weeksAgo} weeks ago`;
    } else if (monthsAgo < 12) {
        return `${monthsAgo} months ago`;
    } else {
        return `${yearsAgo} years ago`;
    }
}
